/**
 * CreateSupplierModal — minimal admin form for creating a Supplier doc.
 * Wraps ModalShell (do NOT mimic ToolPickerModal's inline chrome).
 *
 * Validation:
 *   supplier_name: required (trim, non-empty)
 *   duns:          optional; if present after trim, must match /^\d{9}$/
 *
 * Submit: POST manage_supplier; on 201 calls onCreated(newDoc) (parent
 * closes the modal). On 4xx/5xx, shows inline error and stays open.
 */

import React, { useState, useEffect } from 'react';
import ModalShell from '../../../shared/components/ModalShell';
import { createSupplier } from '../supplierManagementApi';
import './CreateSupplierModal.css';

const DUNS_PATTERN = /^\d{9}$/;
const DUNS_ERROR_MESSAGE = 'DUNS must be 9 digits, or leave blank';

export default function CreateSupplierModal({
  isOpen,
  onClose,
  onCreated,
  initialName = '',
  callerEmail,
}) {
  const [supplierName, setSupplierName] = useState(initialName || '');
  const [duns, setDuns] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Reset form whenever the modal (re-)opens so a previous failed attempt
  // doesn't bleed into the next one.
  useEffect(() => {
    if (isOpen) {
      setSupplierName(initialName || '');
      setDuns('');
      setError(null);
      setSubmitting(false);
    }
  }, [isOpen, initialName]);

  const trimmedName = supplierName.trim();
  const trimmedDuns = duns.trim();
  const canSubmit = trimmedName.length > 0 && !submitting;

  async function handleSubmit() {
    setError(null);

    if (!trimmedName) {
      setError('Supplier name is required.');
      return;
    }
    if (trimmedDuns && !DUNS_PATTERN.test(trimmedDuns)) {
      setError(DUNS_ERROR_MESSAGE);
      return;
    }

    const payload = { supplier_name: trimmedName };
    if (trimmedDuns) payload.duns = trimmedDuns;

    try {
      setSubmitting(true);
      const newDoc = await createSupplier(callerEmail, payload);
      onCreated(newDoc);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('CreateSupplierModal submit error:', err);
      setError(err.message || 'Failed to create supplier.');
    } finally {
      setSubmitting(false);
    }
  }

  const footer = (
    <div className="csm-footer">
      <button
        type="button"
        className="csm-btn csm-btn--secondary"
        onClick={onClose}
        disabled={submitting}
      >
        Cancel
      </button>
      <button
        type="button"
        className="csm-btn csm-btn--primary"
        onClick={handleSubmit}
        disabled={!canSubmit}
      >
        {submitting ? 'Creating…' : 'Create'}
      </button>
    </div>
  );

  return (
    <ModalShell
      isOpen={isOpen}
      onClose={onClose}
      title="Create new supplier"
      footer={footer}
      className="csm-dialog"
    >
      <div className="csm-form">
        <label className="csm-field">
          <span className="csm-label">Supplier name</span>
          <input
            type="text"
            className="csm-input"
            value={supplierName}
            onChange={(e) => setSupplierName(e.target.value)}
            placeholder="Supplier name"
            autoFocus
          />
        </label>

        <label className="csm-field">
          <span className="csm-label">
            DUNS <span className="csm-label-hint">(optional)</span>
          </span>
          <input
            type="text"
            className="csm-input"
            value={duns}
            onChange={(e) => setDuns(e.target.value)}
            placeholder="9-digit DUNS or leave blank"
            inputMode="numeric"
          />
        </label>

        {error && <div className="csm-error" role="alert">{error}</div>}
      </div>
    </ModalShell>
  );
}
